/* eslint-disable @typescript-eslint/no-unsafe-assignment */

export class IndexedDatabase<T> {
  name: string;

  objectStore: IDBObjectStore | undefined;

  database: IDBDatabase | undefined;

  newData: T[];

  objectStoreOption: IDBObjectStoreParameters;

  constructor(name: string, indexes: string[], newData?: T[], objectStoreOptions?: IDBObjectStoreParameters) {
    this.name = name;
    this.newData = newData ?? [];
    this.objectStoreOption = objectStoreOptions ?? { autoIncrement: true };
    this._initDb(newData, indexes);
  }

  async count(): Promise<number> {
    return await new Promise(resolve => {
      const request = this._openConnection();
      request.onsuccess = () => {
        if (this.database) {
          const objectStore = this.database.transaction([this.name], 'readonly').objectStore(this.name);

          const countRequest = objectStore.count();
          countRequest.onsuccess = () => {
            resolve(countRequest.result);
          };
        }
      };
    });
  }

  async overwriteCurrentData(data: T[]): Promise<void> {
    await this._clearData();
    await this.insertData(data);
  }

  async getData(): Promise<T[]> {
    return await new Promise(resolve => {
      const request = this._openConnection();

      request.onsuccess = () => {
        if (this.database) {
          const cursorRequest = this.database.transaction(this.name).objectStore(this.name).index('id').openCursor();

          const result: T[] = [];

          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;

            if (cursor) {
              const { value } = cursor;
              result.push(value);
              cursor.continue();
            } else {
              resolve(result);
            }
          };
        }
      };
    });
  }

  async _initDb(newData?: T[], indexes?: string[]): Promise<void> {
    return await new Promise(resolve => {
      const request = this._openConnection();

      request.onupgradeneeded = () => {
        const newDatabase = request.result;
        this.database = request.result;

        if (!newDatabase.objectStoreNames.contains(this.name)) {
          const objectStore = newDatabase.createObjectStore(this.name, { keyPath: 'id' });
          indexes?.map(item => objectStore.createIndex(item, item));
        }
      };

      request.onsuccess = async () => {
        this.database = request.result;
        if (newData) {
          await this.insertData(newData);
        }
        resolve();
      };
    });
  }

  _openConnection(): IDBOpenDBRequest {
    return indexedDB.open(this.name);
  }

  async insertData(data: T[]): Promise<void> {
    return await new Promise(resolve => {
      const request = this._openConnection();
      request.onsuccess = () => {
        if (this.database) {
          const objectStore = this.database.transaction(this.name, 'readwrite').objectStore(this.name);

          data.forEach(dataItem => {
            objectStore.add(dataItem);
          });
        }
        resolve();
      };
    });
  }

  async _clearData(): Promise<void> {
    return await new Promise(resolve => {
      const request = this._openConnection();

      request.onsuccess = () => {
        if (this.database) {
          this.database.transaction(this.name, 'readwrite').objectStore(this.name).clear();
        }
        resolve();
      };
    });
  }
}
